import { useState, useCallback, useRef, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { RootDispatch, RootState } from "../store";
import { getSalesRep } from "../store/slices/epcSlice";

export const useInfiniteSalesReps = (resetTrigger?: any) => {
  const dispatch: RootDispatch = useDispatch();
  const [searchTerm, setSearchTerm] = useState("");
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [allSalesReps, setAllSalesReps] = useState<any[]>([]);

  const totalPages = useSelector(
    (state: RootState) => state.epc.totalPagesOfSalesReps,
  );
  const salesRepsFromRedux = useSelector(
    (state: RootState) => state.epc.salesReps || [],
  );

  const totalPagesRef = useRef(0);

  useEffect(() => {
    totalPagesRef.current = totalPages;
  }, [totalPages]);

  useEffect(() => {
    setPage(1);
    setAllSalesReps([]);
    setHasMore(true);

    if (resetTrigger) {
      fetchSalesReps(1, searchTerm);
    }
  }, [resetTrigger]);

  // Reset and fetch first page when search term changes
  useEffect(() => {
    setPage(1);
    setAllSalesReps([]);
    setHasMore(true);
    fetchSalesReps(1, searchTerm);
  }, [searchTerm]);

  // Update allSalesReps when Redux data changes
  useEffect(() => {
    if (page === 1) {
      setAllSalesReps(salesRepsFromRedux);
    } else {
      setAllSalesReps((prev) => [...prev, ...salesRepsFromRedux]);
    }
  }, [salesRepsFromRedux, page]);

  const fetchSalesReps = async (pageNum: number, search?: string) => {
    setIsLoadingMore(true);
    try {
      await dispatch(
        getSalesRep({
          search: search || undefined,
          page: pageNum,
          limit: 10,
        }),
      );
      setHasMore(pageNum < totalPagesRef.current);
    } catch (error) {
      console.error("Error fetching sales reps:", error);
    } finally {
      setIsLoadingMore(false);
    }
  };

  const loadMore = useCallback(() => {
    if (!isLoadingMore && hasMore && page < totalPagesRef.current) {
      const nextPage = page + 1;
      setPage(nextPage);
      fetchSalesReps(nextPage, searchTerm);
    }
  }, [isLoadingMore, hasMore, page, searchTerm]);

  const handleSearch = useCallback((inputValue: string) => {
    setSearchTerm(inputValue);
  }, []);

  const reset = useCallback(() => {
    setSearchTerm("");
    setPage(1);
    setHasMore(true);
    setAllSalesReps([]);

    setTimeout(() => {
      fetchSalesReps(1, "");
    }, 0);
  }, [fetchSalesReps]);

  return {
    salesReps: allSalesReps,
    isLoadingMore,
    hasMore,
    loadMore,
    handleSearch,
    searchTerm,
    reset,
  };
};
